/** Master data: everything the firm configures, in one place.
 *
 *  The database is the only source of truth, so this screen is the only way in for
 *  people, companies, holidays and settings — there is no file on the server to edit
 *  and nothing to import. `backend/scripts/admin.py` exists for the one case this
 *  screen cannot cover: nobody can sign in at all.
 *
 *  Four sections rather than four pages, because the four are edited together: adding
 *  a company usually means naming its approver, and adding a person usually means
 *  picking which companies they work on.
 */
import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Company, type Holiday, type Invite, type Person } from '@/api/client'
import { stampDate, Banner, ErrorNote, Spinner, Tag, fmtDate, todayISO, yearNow } from '@/components/ui'
import { QrCode } from '@/components/QrCode'
import { useAuth } from '@/lib/auth'
import { useChannel } from '@/lib/channel'

/** Whether somebody can sign in. Deliberately not `StatusChip`: that carries the task
 *  palette, and reusing it made an active person read "Completed". */
function ActiveChip({ active }: { active: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs
      font-medium whitespace-nowrap ${
        active ? 'bg-done-tint text-done-ink' : 'bg-rule text-ink-3'}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-done' : 'bg-ink-3'}`}
            aria-hidden />
      {active ? 'Active' : 'Locked out'}
    </span>
  )
}

const TABS = [
  { key: 'people', label: 'People' },
  { key: 'companies', label: 'Companies' },
  { key: 'holidays', label: 'Holidays' },
  { key: 'settings', label: 'Settings' },
] as const
type Tab = (typeof TABS)[number]['key']

export function MasterData() {
  const [tab, setTab] = useState<Tab>('people')
  return (
    <>
      <div className="flex gap-1 mb-4 border-b border-rule">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            className={`px-3.5 py-2 text-[13px] border-b-2 -mb-px transition-colors ${
              tab === t.key ? 'border-primary text-primary font-semibold'
                            : 'border-transparent text-ink-2 hover:text-ink'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'people' ? <PeopleSection /> : null}
      {tab === 'companies' ? <CompaniesSection /> : null}
      {tab === 'holidays' ? <HolidaysSection /> : null}
      {tab === 'settings' ? <SettingsSection /> : null}
    </>
  )
}

// ------------------------------------------------------------------ people
const ROLES = ['OWNER', 'APPROVER', 'RESPONSIBLE']

function PeopleSection() {
  const qc = useQueryClient()
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<Person[]>('/users') })
  const ch = useChannel()
  const [editing, setEditing] = useState<Person | null>(null)
  const [adding, setAdding] = useState(false)
  const [err, setErr] = useState<unknown>(null)

  // Waiting on the channel too, so the forms below never render one channel's
  // controls and then swap them for the other's - one of them is a consent tick.
  if (users.isLoading || ch.loading) return <Spinner />
  if (users.error) return <ErrorNote error={users.error} />
  if (ch.error) return <ErrorNote error={ch.error} />

  const rows = users.data ?? []
  const me = rows.find((u) => u.is_superuser)
  // A person needs several things at once to be reachable, and when one is missing
  // nothing is sent and nothing complains.
  const unreachable = rows.filter((u) => u.active && !u.notifiable)
  // The install's own answer, not `rows[0].channel`: that read a page-level fact off
  // the first row of a list, so an install with nobody in it yet reported WhatsApp.
  const { onTelegram, label: channelName } = ch

  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      {unreachable.length > 0 ? (
        <Banner tone="warn">
          {unreachable.length} active {unreachable.length === 1 ? 'person' : 'people'} will
          not receive {channelName} messages.{' '}
          {onTelegram
            ? 'Each person has to tap an invite link and start the bot themselves — click Invite to send them one.'
            : 'A person is only reachable when they are active, opted in, and have a phone number — click Edit to fix.'}
        </Banner>
      ) : null}

      <div className="card mb-4">
        <div className="card-head">
          <b className="text-[13px]">People</b>
          <span className="text-[11.8px] text-ink-3">
            Who can sign in, who approves, and who can be reached on {channelName}
          </span>
          <span className="flex-1" />
          <button className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>
            Add person
          </button>
        </div>
        <div className="table-wrap">
          <table className="w-full border-collapse">
            <thead><tr>
              <th className="th">Name</th><th className="th w-40">Phone</th>
              <th className="th w-52">Roles</th>
              <th className="th w-44">Reporting</th>
              <th className="th w-28">Active</th>
              <th className="th w-56">{channelName}</th>
              <th className="th w-44"></th>
            </tr></thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.user_id}>
                  <td className="td">
                    <b className="font-semibold">{u.name}</b>
                    {u.is_superuser ? (
                      <span className="ml-1.5 align-middle"><Tag>superuser</Tag></span>
                    ) : null}
                    <span className="block text-[11.5px] text-ink-3">{u.email}</span>
                  </td>
                  <td className="td mono text-[12.5px]">
                    {u.phone || <span className="text-ink-3">— none</span>}
                  </td>
                  <td className="td">
                    <span className="flex gap-1 flex-wrap">
                      {u.roles.map((r) => <Tag key={r}>{r.toLowerCase()}</Tag>)}
                    </span>
                  </td>
                  {/* Both directions in one cell, because they are one fact read
                      from two ends: a team, and where this person sits in somebody
                      else's. Without it the tree is only legible by opening people
                      one at a time, which is exactly the work the team list exists
                      to remove. */}
                  <td className="td text-[12.5px]">
                    {u.direct_reports ? (
                      <span>
                        {u.direct_reports} report{u.direct_reports === 1 ? '' : 's'}
                        {u.can_enter_tasks
                          ? <span className="block text-[11px] text-done-ink">
                              enters their tasks
                            </span>
                          : null}
                      </span>
                    ) : null}
                    {u.reports_to ? (
                      <span className="block text-[11px] text-ink-3">
                        under {rows.find((x) => x.user_id === u.reports_to)?.name
                               ?? u.reports_to}
                      </span>
                    ) : null}
                    {!u.direct_reports && !u.reports_to
                      ? <span className="text-ink-3">—</span> : null}
                  </td>
                  <td className="td"><ActiveChip active={u.active} /></td>
                  <td className="td">
                    {u.notifiable
                      ? <span className="text-[12.5px] text-done-ink">
                          receives messages{sinceLabel(u, onTelegram)}
                        </span>
                      : <span className="text-[12.5px] text-ink-3">
                          {u.why_not_notifiable || 'will not be notified'}
                        </span>}
                  </td>
                  <td className="td">
                    <span className="flex gap-1.5 flex-wrap">
                      {onTelegram && !u.telegram_chat_id ? <InviteButton user={u} /> : null}
                      <button className="btn btn-sm" onClick={() => setEditing(u)}>Edit</button>
                      {u.is_superuser ? null : <DeletePerson user={u} onError={setErr} />}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 text-[11.8px] text-ink-3">
          Deactivating locks somebody out and keeps every task of theirs readable.
          Deleting is only offered when nothing points at them at all — which is why
          most people are deactivated, not deleted.
        </div>
      </div>

      <SuperuserCard holder={me} everyone={rows} onError={setErr} />

      {editing ? <EditPerson user={editing} onClose={() => setEditing(null)} /> : null}
      {adding ? (
        <AddPerson onClose={() => setAdding(false)}
                   onDone={() => { setAdding(false); qc.invalidateQueries({ queryKey: ['users'] }) }} />
      ) : null}
    </>
  )
}

function DeletePerson({ user, onError }: { user: Person; onError: (e: unknown) => void }) {
  const qc = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const del = useMutation({
    mutationFn: () => api.del(`/users/${user.user_id}`),
    onSuccess: () => {
      setConfirming(false)
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['companies'] })
    },
    // The backend refuses with a count of what is in the way and names the remedy.
    // Showing that verbatim beats a generic "could not delete".
    onError: (e) => { setConfirming(false); onError(e) },
  })

  if (!confirming) {
    return <button className="btn btn-sm" onClick={() => setConfirming(true)}>Delete</button>
  }
  return (
    <span className="flex gap-1.5 items-center">
      <button className="btn btn-sm btn-bad" disabled={del.isPending}
              onClick={() => del.mutate()}>
        {del.isPending ? '…' : 'Really delete'}
      </button>
      <button className="btn btn-sm" onClick={() => setConfirming(false)}>No</button>
    </span>
  )
}

function SuperuserCard({ holder, everyone, onError }: {
  holder?: Person; everyone: Person[]; onError: (e: unknown) => void
}) {
  const qc = useQueryClient()
  const { me } = useAuth()
  const [to, setTo] = useState('')
  const hand = useMutation({
    mutationFn: () => api.post(`/users/${to}/superuser`),
    onSuccess: () => { setTo(''); qc.invalidateQueries({ queryKey: ['users'] }) },
    onError,
  })
  // Only the holder may hand it over — an owner who could *take* it could then remove
  // every other owner, which is the situation the superuser exists to prevent. So the
  // question is whether *I* hold it, not whether the holder is an owner (they always
  // are). The backend refuses either way; this only decides what to show.
  const iHoldIt = !!holder && !!me && holder.user_id === me.user_id
  const candidates = everyone.filter((u) => u.active && !u.is_superuser)

  return (
    <div className="card">
      <div className="card-head"><b className="text-[13px]">The superuser</b></div>
      <div className="p-4">
        <p className="text-[13px]">
          {holder
            ? <><b>{holder.name}</b> <span className="mono text-[12px] text-ink-3">
                {holder.email}</span></>
            : <span className="text-ink-3">Nobody holds it.</span>}
        </p>
        <p className="text-[11.8px] text-ink-3 mt-1.5 max-w-2xl">
          There is exactly one, and they always count as an owner whether or not the
          role is ticked. They cannot be deleted or locked out, so an install can never
          end up with nobody able to administer it. Only the superuser can hand it
          over, and doing so changes nothing else about either account.
        </p>
        {iHoldIt ? (
          <div className="flex gap-2 mt-3.5 items-end">
            <div className="w-full sm:w-72">
              <label className="label">Hand it to</label>
              <select className="field" value={to} onChange={(e) => setTo(e.target.value)}>
                <option value="">Choose somebody…</option>
                {candidates.map((u) => (
                  <option key={u.user_id} value={u.user_id}>{u.name} — {u.email}</option>
                ))}
              </select>
            </div>
            <button className="btn" disabled={!to || hand.isPending}
                    onClick={() => hand.mutate()}>
              {hand.isPending ? 'Handing over…' : 'Hand over'}
            </button>
          </div>
        ) : (
          <p className="text-[11.8px] text-ink-3 mt-3">
            Only {holder?.name ?? 'the superuser'} can hand this over. If they are
            unavailable, it is moved from the server with{' '}
            <span className="mono">admin.py user superuser --to</span>.
          </p>
        )}
      </div>
    </div>
  )
}

function AddPerson({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [err, setErr] = useState<unknown>(null)
  const [form, setForm] = useState({
    name: '', email: '', phone: '', roles: ['RESPONSIBLE'] as string[],
    active: true, whatsapp_opt_in: false, notes: '',
  })
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))
  const { onTelegram } = useChannel()
  const save = useMutation({
    // Same omission as the edit form: `POST /users` defaults the flag to false when
    // the key is absent, so a Telegram install never records a WhatsApp consent
    // nobody was asked for.
    mutationFn: () => {
      const { whatsapp_opt_in, ...rest } = form
      return api.post('/users', onTelegram ? rest : { ...rest, whatsapp_opt_in })
    },
    onSuccess: onDone,
    onError: setErr,
  })

  return (
    <Drawer title="Add person" onClose={onClose} busy={save.isPending}
            ready={form.roles.length > 0}
            onSave={() => { setErr(null); save.mutate() }}>
      {err ? <ErrorNote error={err} /> : null}
      <div>
        <label className="label">Name</label>
        <input className="field" value={form.name} autoFocus
               onChange={(e) => set('name', e.target.value)} />
      </div>
      <div>
        <label className="label">Email — this is their sign-in</label>
        <input className="field" value={form.email}
               onChange={(e) => set('email', e.target.value)} />
        <p className="text-[11.5px] text-ink-3 mt-1">
          They get no password here. Tell them to use “Forgot / set password” on the
          sign-in screen, which emails them a one-time code.
        </p>
      </div>
      <div>
        <label className="label">Roles</label>
        <div className="flex gap-3 flex-wrap">
          {ROLES.map((r) => (
            <label key={r} className="flex items-center gap-1.5 text-[12.5px]">
              <input type="checkbox" checked={form.roles.includes(r)}
                     onChange={() => set('roles', form.roles.includes(r)
                       ? form.roles.filter((x) => x !== r) : [...form.roles, r])} />
              {r.toLowerCase()}
            </label>
          ))}
        </div>
        {form.roles.length === 0 && (
          <p className="text-[11.5px] text-pend-ink mt-1.5">
            Pick at least one. Somebody with no role can sign in but cannot do
            anything, which looks like a broken app rather than an unfinished account.
          </p>
        )}
      </div>
      <div className="border-t border-rule pt-3.5">
        <label className="label">Phone</label>
        <input className="field mono" type="tel" inputMode="tel" value={form.phone} placeholder="+91 98200 11234"
               onChange={(e) => set('phone', e.target.value)} />
        {onTelegram ? (
          <p className="text-[11.5px] text-ink-3 mt-1">
            For your records — Telegram messages go to their linked account, not to
            this number. <b>They will receive nothing until they tap an invite:</b>{' '}
            save, then click <b>Invite</b> on their row.
          </p>
        ) : (
          <>
            <label className="flex items-center gap-2 text-[13px] mt-2.5">
              <input type="checkbox" checked={form.whatsapp_opt_in}
                     onChange={(e) => set('whatsapp_opt_in', e.target.checked)} />
              They have agreed to receive WhatsApp messages
            </label>
            <p className="text-[11.5px] text-ink-3 mt-1">
              Ticking this records <b>you</b> confirming they agreed, not them
              agreeing. Ask first — WhatsApp requires consent, and Meta counts
              complaints against the number.
            </p>
          </>
        )}
      </div>
      <div>
        <label className="label">Notes</label>
        <input className="field" value={form.notes}
               onChange={(e) => set('notes', e.target.value)} />
      </div>
    </Drawer>
  )
}

// --------------------------------------------------------------- companies
function CompaniesSection() {
  const qc = useQueryClient()
  const companies = useQuery({ queryKey: ['companies'], queryFn: () => api.get<Company[]>('/companies') })
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<Person[]>('/users') })
  const [editing, setEditing] = useState<Company | null>(null)
  const [adding, setAdding] = useState(false)
  const [err, setErr] = useState<unknown>(null)

  if (companies.isLoading || users.isLoading) return <Spinner />
  if (companies.error) return <ErrorNote error={companies.error} />

  const rows = [...(companies.data ?? [])].sort(
    (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
  const people = users.data ?? []
  const nameOf = (id: string) => people.find((u) => u.user_id === id)?.name ?? id

  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      <div className="card">
        <div className="card-head">
          <b className="text-[13px]">Companies</b>
          <span className="text-[11.8px] text-ink-3">
            The client list every task is filed against
          </span>
          <span className="flex-1" />
          <button className="btn btn-sm btn-primary" onClick={() => setAdding(true)}>
            Add company
          </button>
        </div>
        <div className="table-wrap">
          <table className="w-full border-collapse">
            <thead><tr>
              <th className="th">Company</th><th className="th w-32">Short name</th>
              <th className="th w-48">Default approver</th><th className="th w-20">Order</th>
              <th className="th w-28">Status</th><th className="th w-40"></th>
            </tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.company_id}>
                  <td className="td">
                    <b className="font-semibold">{c.name}</b>
                    <span className="block mono text-[11px] text-ink-3">{c.company_id}</span>
                  </td>
                  <td className="td mono text-[12.5px]">{c.short_name || '—'}</td>
                  <td className="td text-[12.5px]">
                    {c.default_approver_user_id
                      ? nameOf(c.default_approver_user_id)
                      : <span className="text-ink-3">— none</span>}
                  </td>
                  <td className="td mono text-xs">{c.sort_order}</td>
                  <td className="td text-[12.5px]">
                    {c.active ? 'Active' : <span className="text-ink-3">Archived</span>}
                  </td>
                  <td className="td">
                    <span className="flex gap-1.5 flex-wrap">
                      <button className="btn btn-sm" onClick={() => setEditing(c)}>Edit</button>
                      <DeleteCompany company={c} onError={setErr} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 text-[11.8px] text-ink-3">
          Archiving removes a company from new-task dropdowns and leaves every existing
          task intact. Deleting is only offered while no task has ever been filed
          against it — including tasks that carry only the name.
        </div>
      </div>

      {editing ? (
        <EditCompany company={editing} approvers={people}
                     onClose={() => setEditing(null)} />
      ) : null}
      {adding ? (
        <EditCompany approvers={people} onClose={() => setAdding(false)}
                     onDone={() => { setAdding(false); qc.invalidateQueries({ queryKey: ['companies'] }) }} />
      ) : null}
    </>
  )
}

function DeleteCompany({ company, onError }: { company: Company; onError: (e: unknown) => void }) {
  const qc = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const del = useMutation({
    mutationFn: () => api.del(`/companies/${company.company_id}`),
    onSuccess: () => { setConfirming(false); qc.invalidateQueries({ queryKey: ['companies'] }) },
    onError: (e) => { setConfirming(false); onError(e) },
  })
  if (!confirming) {
    return <button className="btn btn-sm" onClick={() => setConfirming(true)}>Delete</button>
  }
  return (
    <span className="flex gap-1.5 items-center">
      <button className="btn btn-sm btn-bad" disabled={del.isPending}
              onClick={() => del.mutate()}>
        {del.isPending ? '…' : 'Really delete'}
      </button>
      <button className="btn btn-sm" onClick={() => setConfirming(false)}>No</button>
    </span>
  )
}

/** One form for both adding and editing: the fields are identical, and two copies
 *  would drift on which of them is required. */
function EditCompany({ company, approvers, onClose, onDone }: {
  company?: Company; approvers: Person[]; onClose: () => void; onDone?: () => void
}) {
  const qc = useQueryClient()
  const [err, setErr] = useState<unknown>(null)
  const [form, setForm] = useState({
    name: company?.name ?? '',
    short_name: company?.short_name ?? '',
    active: company?.active ?? true,
    default_approver_user_id: company?.default_approver_user_id ?? '',
    sort_order: company?.sort_order ?? 0,
  })
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const save = useMutation({
    mutationFn: () => company
      ? api.patch(`/companies/${company.company_id}`, form)
      : api.post('/companies', form),
    onSuccess: () => {
      // The task list shows company names and the new-task dropdown comes from here.
      qc.invalidateQueries({ queryKey: ['companies'] })
      onDone ? onDone() : onClose()
    },
    onError: setErr,
  })

  // Only somebody who can actually approve, or the list quietly routes every approval
  // to a person who is never asked. The backend refuses this too.
  const canApprove = approvers.filter((u) => u.roles.includes('APPROVER') || u.is_owner)

  return (
    <Drawer title={company ? 'Edit company' : 'Add company'} onClose={onClose}
            busy={save.isPending} onSave={() => { setErr(null); save.mutate() }}>
      {err ? <ErrorNote error={err} /> : null}
      <div>
        <label className="label">Name</label>
        <input className="field" value={form.name} autoFocus
               onChange={(e) => set('name', e.target.value)} />
        {company ? (
          <p className="text-[11.5px] text-ink-3 mt-1">
            Renaming does not rewrite existing tasks: those keep the name the work was
            filed under, so a report for last month still reads correctly.
          </p>
        ) : null}
      </div>
      <div>
        <label className="label">Short name — used where space is tight</label>
        <input className="field mono" value={form.short_name}
               onChange={(e) => set('short_name', e.target.value)} />
      </div>
      <div>
        <label className="label">Default approver</label>
        <select className="field" value={form.default_approver_user_id}
                onChange={(e) => set('default_approver_user_id', e.target.value)}>
          <option value="">— none, chosen per task</option>
          {canApprove.map((u) => (
            <option key={u.user_id} value={u.user_id}>{u.name}</option>
          ))}
        </select>
        <p className="text-[11.5px] text-ink-3 mt-1">
          Only people who can approve are listed. Anyone else would sit in the field
          and never be asked for anything.
        </p>
      </div>
      <div>
        <label className="label">Sort order — where it sits in the dropdown</label>
        <input className="field mono" type="number" value={form.sort_order}
               onChange={(e) => set('sort_order', Number(e.target.value))} />
      </div>
      <div>
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={form.active}
                 onChange={(e) => set('active', e.target.checked)} />
          Active — appears when filing a new task
        </label>
        <p className="text-[11.5px] text-ink-3 mt-1">
          Turning this off archives it. Every existing task is untouched; it simply
          stops being offered for new ones.
        </p>
      </div>
    </Drawer>
  )
}

// ---------------------------------------------------------------- holidays
const HOLIDAY_TYPES = [
  { value: 'PUBLIC', label: 'Public holiday' },
  { value: 'OFFICE_CLOSED', label: 'Office closed' },
  { value: 'WORKING_DAY', label: 'Working day override' },
]

function HolidaysSection() {
  const qc = useQueryClient()
  const holidays = useQuery({ queryKey: ['holidays'], queryFn: () => api.get<Holiday[]>('/holidays') })
  const [form, setForm] = useState({ date: '', name: '', type: 'PUBLIC' })
  const [err, setErr] = useState<unknown>(null)

  const invalidate = () => {
    setErr(null)
    qc.invalidateQueries({ queryKey: ['holidays'] })
    qc.invalidateQueries({ queryKey: ['calendar'] })   // the year grid repaints
    qc.invalidateQueries({ queryKey: ['day'] })        // and today's banner
  }
  const add = useMutation({
    mutationFn: () => api.post('/holidays', form),
    onSuccess: () => { setForm({ date: '', name: '', type: 'PUBLIC' }); invalidate() },
    onError: setErr,
  })
  const remove = useMutation({
    mutationFn: (date: string) => api.del(`/holidays/${date}`),
    onSuccess: invalidate,
    onError: setErr,
  })

  if (holidays.isLoading) return <Spinner />
  if (holidays.error) return <ErrorNote error={holidays.error} />

  const rows = holidays.data ?? []
  const today = todayISO()
  const upcoming = rows.filter((h) => h.date >= today)
  const past = rows.filter((h) => h.date < today)

  const table = (list: Holiday[], title: string, note?: string) => (
    <div className="card mb-4">
      <div className="card-head">
        <b className="text-[13px]">{title}</b>
        {note ? <span className="text-[11.8px] text-ink-3">{note}</span> : null}
        <span className="flex-1" />
        <span className="mono text-[11.5px] text-ink-3">{list.length}</span>
      </div>
      {list.length === 0 ? (
        <p className="px-4 py-3 text-[12.5px] text-ink-3">None.</p>
      ) : (
        <div className="table-wrap">
          <table className="w-full border-collapse">
            <thead><tr>
              <th className="th w-56">Date</th><th className="th">Name</th>
              <th className="th w-48">Kind</th><th className="th w-28"></th>
            </tr></thead>
            <tbody>
              {list.map((h) => (
                <tr key={h.date}>
                  <td className="td">{fmtDate(h.date)}</td>
                  <td className="td">{h.name || <span className="text-ink-3">—</span>}</td>
                  <td className="td text-[12.5px]">
                    {HOLIDAY_TYPES.find((t) => t.value === h.type)?.label ?? h.type}
                  </td>
                  <td className="td">
                    <button className="btn btn-sm" disabled={remove.isPending}
                            onClick={() => remove.mutate(h.date)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      <div className="card mb-4">
        <div className="card-head"><b className="text-[13px]">Add a holiday</b></div>
        <div className="p-4 flex gap-2.5 items-end flex-wrap">
          <div className="w-44">
            <label className="label">Date</label>
            <input className="field" type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div className="flex-1 min-w-52">
            <label className="label">Name</label>
            <input className="field" value={form.name} placeholder="Diwali"
                   onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="w-52">
            <label className="label">Kind</label>
            <select className="field" value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {HOLIDAY_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" disabled={!form.date || add.isPending}
                  onClick={() => { setErr(null); add.mutate() }}>
            {add.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
        <p className="px-4 pb-3 text-[11.8px] text-ink-3">
          A working-day override is the opposite case: a Sunday the firm does work.
          Adding a date that is already listed replaces it. The{' '}
          <b>Working calendar</b> screen shows the whole year repainted, which is how
          you check a pattern rather than discovering it in March.
        </p>
      </div>

      <SuggestionPicker known={new Set(rows.map((h) => h.date))}
                        onAdded={invalidate} onError={setErr} />

      {table(upcoming, 'Upcoming', 'These are what change where new work lands')}
      {table(past, 'Past', 'Kept because reports read over dates that have gone by')}
    </>
  )
}

// ------------------------------------------------------- the suggestion picker
type Suggestion = {
  date: string; name: string; scope: string
  moon_dependent: boolean; already: boolean; already_as: string
}
type Suggestions = {
  year: number; country_name: string; state_name: string
  years_covered: number[]; complete: boolean; note: string
  holidays: Suggestion[]
}

/** The public holidays for the firm's country and state, offered rather than applied.
 *
 *  Nothing here reaches the calendar without being ticked. That is the whole contract:
 *  a firm that works through a festival most of the country takes off should not have
 *  that decided for it by a table in the repository, and a calendar that quietly
 *  acquired a holiday would send no digest on a day people were working.
 *
 *  Which is also why the form above survives untouched. The list is allowed to be
 *  incomplete precisely because typing one in by hand is still right there.
 */
function SuggestionPicker({ known, onAdded, onError }: {
  known: Set<string>
  onAdded: () => void
  onError: (e: unknown) => void
}) {
  const [year, setYear] = useState(yearNow())
  const [ticked, setTicked] = useState<Set<string>>(new Set())

  const q = useQuery({
    queryKey: ['holiday-suggestions', year],
    queryFn: () => api.get<Suggestions>(`/holidays/suggestions?year=${year}`),
  })
  const add = useMutation({
    mutationFn: () => api.post('/holidays/bulk',
                               { year, dates: [...ticked] }),
    onSuccess: () => { setTicked(new Set()); onAdded() },
    onError,
  })

  const move = (by: number) => { setYear(year + by); setTicked(new Set()) }
  const toggle = (d: string) => setTicked((s) => {
    const next = new Set(s)
    if (next.has(d)) next.delete(d)
    else next.add(d)
    return next
  })

  const rows = (q.data?.holidays ?? []).filter((h) => !h.already && !known.has(h.date))

  return (
    <div className="card mb-4">
      <div className="card-head">
        <b className="text-[13px]">Public holidays for {year}</b>
        <span className="text-[11.8px] text-ink-3">
          {q.data ? `${q.data.country_name}${
            q.data.state_name && q.data.state_name !== q.data.country_name
              ? ' · ' + q.data.state_name : ''}` : ''}
        </span>
        <span className="flex-1" />
        <button className="btn btn-sm" onClick={() => move(-1)}>←</button>
        <button className="btn btn-sm" onClick={() => move(1)}>→</button>
      </div>

      {q.isLoading ? <div className="p-4"><Spinner /></div>
        : q.error ? <div className="p-4"><ErrorNote error={q.error} /></div>
        : (
        <>
          {q.data?.note ? (
            <p className="px-4 pt-3 text-[11.8px] text-ink-3">{q.data.note}</p>
          ) : null}

          {rows.length === 0 ? (
            <p className="px-4 py-3 text-[12.5px] text-ink-3">
              Everything on the {year} list is already on your calendar.
            </p>
          ) : (
            <>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3
                              gap-x-4 gap-y-1.5">
                {rows.map((h) => (
                  <label key={h.date}
                         className="flex items-baseline gap-2 cursor-pointer">
                    <input type="checkbox" checked={ticked.has(h.date)}
                           onChange={() => toggle(h.date)} />
                    <span className="min-w-0">
                      <span className="text-[12.5px]">{h.name}</span>
                      <span className="mono text-[11px] text-ink-3 ml-1.5">
                        {fmtDate(h.date)}
                      </span>
                      {h.scope === 'state' && (
                        <span className="text-[10.5px] text-ink-3 ml-1.5">state</span>
                      )}
                      {h.moon_dependent && (
                        <span className="block text-[10.5px] text-warn-ink">
                          moves with the moon — confirm nearer the time
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
              <div className="px-4 pb-3 flex items-center gap-2">
                <button className="btn btn-sm"
                        onClick={() => setTicked(new Set(rows.map((h) => h.date)))}>
                  Tick all {rows.length}
                </button>
                {ticked.size > 0 && (
                  <button className="btn btn-sm" onClick={() => setTicked(new Set())}>
                    Clear
                  </button>
                )}
                <span className="flex-1" />
                <button className="btn btn-primary btn-sm"
                        disabled={ticked.size === 0 || add.isPending}
                        onClick={() => add.mutate()}>
                  {add.isPending ? 'Adding…' : `Add ${ticked.size}`}
                </button>
              </div>
              <p className="px-4 pb-3 text-[11.8px] text-ink-3">
                Nothing is added until you tick it. Anything missing goes in with the
                form above, and stays.
              </p>
            </>
          )}
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- settings
/** Keys the Owner should not retype by hand: they are edited on their own screens,
 *  where the change can be shown taking effect. */
const ELSEWHERE: Record<string, string> = {
  working_days: 'Working calendar',
  saturday_working_ordinals: 'Working calendar',
  sunday_working_ordinals: 'Working calendar',
  messaging_channel: 'Message channel',
  timezone: 'Timezone',
  // Editable here only by the superuser. Shown read-only for everybody else would
  // need a third state; pointing at the screen that owns it is simpler and is where
  // the warning about the digest lives anyway.
  recurring_generate_time: 'Recurring',
  recurring_enabled: 'Recurring',
}
/** Keys with a fixed set of answers. A typo in `country` does not fail loudly - it
 *  produces an empty holiday list, which reads as "there are no holidays" rather than
 *  "you have typed IND instead of IN". A dropdown removes the failure mode.
 *
 *  These mirror `domain/holiday_catalogue.py`; both change together when a region is
 *  added, which is the only time either changes at all. */
const CHOICES: Record<string, { value: string; label: string }[]> = {
  country: [{ value: 'IN', label: 'India' }],
  state: [
    { value: 'GJ', label: 'Gujarat' },
    { value: '', label: 'National holidays only' },
  ],
}

/** Counters and internal markers. Showing them invites editing them, and a hand-set
 *  ref counter hands two tasks the same number. */
const HIDDEN = (k: string) =>
  k.startsWith('job_run:') || k.endsWith('_counter') ||
  k === 'instance_id' || k === 'instance_heartbeat' || k === 'token_epoch'

function SettingsSection() {
  const qc = useQueryClient()
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, string>>('/settings'),
  })
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [err, setErr] = useState<unknown>(null)

  const save = useMutation({
    mutationFn: () => api.patch('/settings', edits),
    onSuccess: () => {
      setEdits({}); setErr(null)
      qc.invalidateQueries({ queryKey: ['settings'] })
      qc.invalidateQueries({ queryKey: ['calendar'] })
      qc.invalidateQueries({ queryKey: ['day'] })
    },
    onError: setErr,
  })

  if (settings.isLoading) return <Spinner />
  if (settings.error) return <ErrorNote error={settings.error} />

  const all = settings.data ?? {}
  const keys = Object.keys(all).filter((k) => !HIDDEN(k)).sort()
  const dirty = Object.keys(edits).length

  return (
    <>
      {err ? <ErrorNote error={err} /> : null}
      <div className="card">
        <div className="card-head">
          <b className="text-[13px]">Settings</b>
          <span className="text-[11.8px] text-ink-3">
            Live values. A change takes effect on the next run of whatever reads it —
            no restart, except the timezone, which is why that one has a screen of its
            own.
          </span>
          <span className="flex-1" />
          {dirty ? (
            <>
              <span className="mono text-[11.5px] text-pend-ink">
                {dirty} unsaved
              </span>
              <button className="btn btn-sm" onClick={() => setEdits({})}>Discard</button>
              <button className="btn btn-sm btn-primary" disabled={save.isPending}
                      onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </>
          ) : null}
        </div>
        <div className="table-wrap">
          <table className="w-full border-collapse">
            <thead><tr>
              <th className="th w-80">Key</th><th className="th">Value</th>
            </tr></thead>
            <tbody>
              {keys.map((k) => {
                const owner = ELSEWHERE[k]
                const value = edits[k] ?? all[k]
                return (
                  <tr key={k}>
                    <td className="td mono text-[12.5px]">
                      {k}
                      {owner ? (
                        <span className="block text-[11px] text-ink-3 font-sans">
                          edited on {owner}
                        </span>
                      ) : null}
                    </td>
                    <td className="td">
                      {owner ? (
                        <span className="mono text-[12.5px] text-ink-3">{all[k] || '—'}</span>
                      ) : CHOICES[k] ? (
                        <select className="field text-[12.5px]" value={value}
                                onChange={(e) => setEdits({ ...edits, [k]: e.target.value })}>
                          {CHOICES[k].map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input className="field mono text-[12.5px]" value={value}
                               onChange={(e) => setEdits({ ...edits, [k]: e.target.value })} />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 text-[11.8px] text-ink-3">
          Counters and job markers are deliberately not listed: setting a ref counter
          by hand would hand two tasks the same number.
        </div>
      </div>
    </>
  )
}

// ------------------------------------------------------------------ drawer
/** The right-hand editing panel, shared by every form on this screen so they open,
 *  close and save the same way. */
function Drawer({ title, onClose, onSave, busy, ready = true, children }: {
  title: string; onClose: () => void; onSave: () => void; busy: boolean
  // Optional, defaulting to true, so the company and holiday drawers are untouched.
  ready?: boolean
  children: ReactNode
}) {
  return (
    <>
      <div className="fixed inset-0 bg-ink/10 z-10" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 w-full sm:w-[480px] bg-surface border-l border-rule
                        shadow-2xl z-20 flex flex-col">
        <div className="px-5 py-3.5 border-b border-rule flex items-center">
          <h3 className="text-base font-semibold tracking-tight">{title}</h3>
          <span className="flex-1" />
          <button onClick={onClose} className="text-ink-3 hover:text-ink text-lg px-1">×</button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-3.5">{children}</div>
        <div className="px-5 py-3.5 border-t border-rule flex gap-2">
          <button className="btn btn-primary" disabled={busy || !ready} onClick={onSave}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </aside>
    </>
  )
}

// --------------------------------------------- lifted from the old People page
/** Choosing an approver's team, at a size where scanning a list stops working.
 *
 *  This was a flat column of every eligible person, always open. That is fine at four
 *  and wrong at twenty in three separate ways: it holds a scroll box open inside a
 *  drawer that already scrolls, it offers no way to find a name, and - worst - it
 *  answers "who is on this team" only by scrolling the box and reading the ticks.
 *
 *  So the two jobs are separated. Choosing happens in a panel that is closed until
 *  wanted and filtered once open; seeing what was chosen happens in a short list that
 *  is always visible, one row per person.
 *
 *  ## Why it expands in place rather than floating
 *
 *  `AddTaskDrawer` already settled this: "a popover inside a drawer is one layer too
 *  many". The popover in `PeoplePicker` exists only for the 176px grid cell, which has
 *  no room to inline. A floating panel over a scrolling drawer would also have to be
 *  re-anchored as the drawer scrolls under it; expanding in flow scrolls with the form
 *  for free. Closed by default, opens on click, ticks inside - a dropdown in every way
 *  that matters to the person using it.
 */
function TeamPicker({ all, candidates, chosen, onToggle, note }: {
  all: Person[]
  /** Who may be placed under them: active, not themselves, never an owner, and nobody
   *  they report up through. Computed by the caller, which has the whole list. */
  candidates: Person[]
  chosen: Set<string>
  onToggle: (id: string) => void
  /** A short aside on one row, or ''. Passed in rather than computed here: moving
   *  somebody's manager is worth warning about and choosing an approver is not, and
   *  the control itself has no business knowing which list it is showing. */
  note?: (p: Person) => string
}) {
  const [open, setOpen] = useState(false)
  const [find, setFind] = useState('')

  const q = find.trim().toLowerCase()
  const matching = q
    ? candidates.filter((p) => p.name.toLowerCase().includes(q))
    : candidates
  // Read from everybody rather than from `candidates`: somebody who has been
  // deactivated is still on the team and must not silently vanish from the list of who
  // is on it - they just cannot be picked from the panel any more.
  const picked = all.filter((p) => chosen.has(p.user_id))
    .sort((a, b) => a.name.localeCompare(b.name))

  const elsewhere = (p: Person) => (note ? note(p) : '')

  return (
    <div>
      <button type="button" className="field text-left flex items-center gap-1.5"
              aria-haspopup="listbox" aria-expanded={open}
              onClick={() => setOpen(!open)}>
        <span className={picked.length ? 'truncate' : 'truncate text-ink-3'}>
          {picked.length === 0
            ? 'Nobody yet'
            : `${picked.length} ${picked.length === 1 ? 'person' : 'people'} chosen`}
        </span>
        <span className="ml-auto text-ink-3 text-[10px]">{open ? '\u25b4' : '\u25be'}</span>
      </button>

      {open && (
        <div className="mt-1 border border-rule rounded">
          <div className="p-2 border-b border-rule">
            {/* The type-to-find a native select would have given for free, and the
                reason twenty names stay workable. */}
            <input className="field text-[12.5px]" autoFocus value={find}
                   placeholder="Find somebody…"
                   onChange={(e) => setFind(e.target.value)}
                   onKeyDown={(e) => {
                     if (e.key === 'Enter') {
                       e.preventDefault()
                       if (matching.length === 1) {
                         onToggle(matching[0].user_id); setFind('')
                       }
                     }
                     if (e.key === 'Escape') { e.preventDefault(); setOpen(false) }
                   }} />
          </div>
          {/* Bounded, so an open panel cannot push the rest of the drawer out of
              reach. Chosen people stay in this list, ticked, rather than moving out of
              it - one list, so there is a single source of truth about the team. */}
          <div className="max-h-[13rem] overflow-auto divide-y divide-rule">
            {candidates.length === 0 ? (
              <p className="text-[12px] text-ink-3 px-2.5 py-2">
                Nobody can be placed under them yet.
              </p>
            ) : matching.length === 0 ? (
              <p className="text-[12px] text-ink-3 px-2.5 py-2">Nobody matches that.</p>
            ) : matching.map((c) => (
              <label key={c.user_id}
                     className="flex items-center gap-2 px-2.5 py-1.5 text-[12.5px]
                                hover:bg-paper cursor-pointer">
                <input type="checkbox" checked={chosen.has(c.user_id)}
                       onChange={() => onToggle(c.user_id)} />
                <span className="truncate">{c.name}</span>
                <span className="ml-auto text-[11px] text-ink-3 whitespace-nowrap">
                  {elsewhere(c)}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Who is on the team, whether or not the panel is open. This is the question the
          control exists to answer, so it is never behind a click. */}
      {picked.length > 0 && (
        <ul className="mt-1.5 border-t border-rule divide-y divide-rule">
          {picked.map((c) => (
            <li key={c.user_id} className="flex items-baseline gap-2 py-1.5">
              <span className="text-[12.5px] truncate">{c.name}</span>
              {/* Email, because it is what every other screen prints under a name -
                  the People table above, the superuser hand-over list. */}
              <span className="text-[11.5px] text-ink-3 truncate">{c.email}</span>
              <span className="flex-1" />
              {elsewhere(c) ? (
                <span className="text-[11px] text-ink-3 whitespace-nowrap">
                  {elsewhere(c)}
                </span>
              ) : null}
              <button type="button" aria-label={`Remove ${c.name}`}
                      className="text-ink-3 hover:text-ink px-1 leading-none"
                      onClick={() => onToggle(c.user_id)}>×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function EditPerson({ user, onClose }: { user: Person; onClose: () => void }) {
  const qc = useQueryClient()
  const [err, setErr] = useState<unknown>(null)
  const [form, setForm] = useState({
    name: user.name, email: user.email, phone: user.phone,
    roles: user.roles, active: user.active,
    whatsapp_opt_in: user.whatsapp_opt_in, notes: user.notes,
    reports_to: user.reports_to ?? '', can_enter_tasks: !!user.can_enter_tasks,
  })
  const [approvers, setApprovers] = useState<Set<string>>(
    new Set(user.approver_ids ?? []))
  const toggleApprover = (id: string) => setApprovers((t) => {
    const next = new Set(t)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  // The team, edited from this end. Seeded lazily rather than in the initialiser
  // because `everybody` has not arrived on the first render; `seeded` makes that a
  // one-time fill instead of a value that snaps back on every refetch.
  const [team, setTeam] = useState<Set<string>>(new Set())
  const [seeded, setSeeded] = useState(false)
  const everybody = useQuery({
    queryKey: ['users'], queryFn: () => api.get<Person[]>('/users'),
  })
  const manager = (everybody.data ?? []).find(
    (p) => p.user_id === (form.reports_to || '\u0000'))

  // Only an approver can have a team at all, so the two controls below appear and
  // disappear with the role tick rather than sitting there refusing to work.
  const isApprover = form.roles.includes('APPROVER') || user.is_owner
  // A team they already have, from the saved tree - not from the ticks in this form,
  // which have not been written yet.
  const hasTeam = (everybody.data ?? []).some((p) => p.reports_to === user.user_id)
  const approverChoices = (everybody.data ?? [])
    .filter((p) => p.active && p.user_id !== user.user_id
                   && (p.roles.includes('APPROVER') || p.is_owner))
    .sort((a, b) => a.name.localeCompare(b.name))

  // Everybody above this person, so they cannot be pulled underneath them - the same
  // circle `below` guards in the other direction.
  const above = (() => {
    const all = everybody.data ?? []
    const out = new Set<string>()
    let at = form.reports_to
    while (at && !out.has(at)) {
      out.add(at)
      at = all.find((p) => p.user_id === at)?.reports_to ?? ''
    }
    return out
  })()
  // Who may be put under this person: active, not themselves, never an owner, and
  // nobody they report up through.
  const teamChoices = (everybody.data ?? [])
    .filter((p) => p.active && p.user_id !== user.user_id && !p.is_owner
                   && !above.has(p.user_id))
    .sort((a, b) => a.name.localeCompare(b.name))
  if (!seeded && everybody.data) {
    setSeeded(true)
    setTeam(new Set(everybody.data.filter((p) => p.reports_to === user.user_id)
                                 .map((p) => p.user_id)))
  }
  const toggleMember = (id: string) => setTeam((t) => {
    const next = new Set(t)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))
  const { onTelegram, label: channelName } = useChannel()

  const save = useMutation({
    // `whatsapp_opt_in` is left OUT of the body on Telegram rather than sent
    // unchanged. PATCH is genuinely partial - the API only touches the flag when the
    // key is present - so omitting it makes "hidden, never cleared" a property of the
    // request instead of a coincidence of how this form seeds its state. That matters
    // because the Owner can switch back to WhatsApp, and the consent has to be
    // exactly as they left it.
    mutationFn: async () => {
      // The tick is forced off with the role rather than sent as the form left it:
      // untick approver on somebody who had entry and the stale `true` would be
      // refused by the server, turning a legitimate edit into an error.
      // `reports_to` is left out with it, and for the same reason PATCH is partial:
      // this drawer no longer edits where they sit, and sending the value back would
      // re-run `assert_can_report_to` on an edge nobody touched - so an unrelated
      // change here would start failing the day their manager goes inactive.
      const { whatsapp_opt_in, reports_to, ...rest } = {
        ...form, approver_ids: [...approvers] }
      void reports_to
      await api.patch(`/users/${user.user_id}`,
                      onTelegram ? rest : { ...rest, whatsapp_opt_in })
      // Then the team, one PATCH per edge that actually moved. Deliberately reusing
      // the same endpoint rather than adding a bulk one: every refusal in
      // `assert_can_report_to` - the circle, the inactive parent, the owner - is
      // already written for it, and a second write path would have to repeat them.
      //
      // The person is saved first because the approver tick may be part of this same
      // edit, and an edge written before the role exists would be refused.
      for (const p of everybody.data ?? []) {
        const want = team.has(p.user_id)
        const has = p.reports_to === user.user_id
        if (want === has) continue
        await api.patch(`/users/${p.user_id}`,
                        { reports_to: want ? user.user_id : '' })
      }
    },
    onSuccess: () => {
      // The task list shows names, and the dropdowns come from this list.
      qc.invalidateQueries({ queryKey: ['users'] })
      onClose()
    },
    onError: setErr,
  })

  // Visible before saving rather than discovered by a message that never arrives.
  // See `reachability` - it follows whichever channel is live.
  const { willReceive, missing } = reachability(form, user, onTelegram)

  const toggleRole = (r: string) =>
    set('roles', form.roles.includes(r)
      ? form.roles.filter((x) => x !== r)
      : [...form.roles, r])

  return (
    <>
      <div className="fixed inset-0 bg-ink/10 z-10" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 w-full sm:w-[480px] bg-surface border-l border-rule
                        shadow-2xl z-20 flex flex-col">
        <div className="px-5 py-3.5 border-b border-rule flex items-center">
          <h3 className="text-base font-semibold tracking-tight">Edit person</h3>
          <span className="flex-1" />
          <button onClick={onClose} className="text-ink-3 hover:text-ink text-lg px-1">×</button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-3.5">
          {err ? <ErrorNote error={err} /> : null}

          <div>
            <label className="label">Name</label>
            <input className="field" value={form.name}
                   onChange={(e) => set('name', e.target.value)} />
          </div>
          <div>
            <label className="label">Email — this is their sign-in</label>
            <input className="field" value={form.email}
                   onChange={(e) => set('email', e.target.value)} />
          </div>

          <div>
            <label className="label">Roles</label>
            <div className="flex gap-3 flex-wrap">
              {ROLES.map((r) => (
                <label key={r} className="flex items-center gap-1.5 text-[12.5px]">
                  <input type="checkbox" checked={form.roles.includes(r)}
                         onChange={() => toggleRole(r)} />
                  {r.toLowerCase()}
                </label>
              ))}
            </div>
            {form.roles.length === 0 && (
              <p className="text-[11.5px] text-pend-ink mt-1.5">
                Pick at least one. Somebody with no role can sign in but cannot do
                anything, which looks like a broken app rather than an unfinished account.
              </p>
            )}
          </div>

          {/* Where they sit, stated rather than edited.
            *
            *  The tree used to be editable from both ends, and building a team that
            *  way means opening every member in turn and picking the same name out of
            *  a dropdown each time. It is edited from the approver's end only now -
            *  one screen, one list of ticks - so this says where they sit and points
            *  at the screen that changes it.
            *
            *  Kept visible rather than dropped: "who is above me" is the fact this
            *  drawer is asked for, and the answer lives nowhere else on the page. */}
          <div>
            <label className="label">Reports to</label>
            <p className="text-[13px]">
              {manager
                ? manager.name
                : <span className="text-ink-3">Nobody</span>}
            </p>
            <p className="text-[11.5px] text-ink-3 mt-1">
              {manager
                ? `Set from ${manager.name}'s own screen — open them and untick this
                   person to move them out.`
                : `Nobody sees their work but the owners. To place them under
                   somebody, open that approver and tick them in Their team.`}
            </p>
          </div>

          {isApprover && (
            <div>
              <label className="label">Their team</label>
              <p className="text-[11.5px] text-ink-3 mb-1.5">
                Everybody who reports to them. Whoever is on this list has their tasks,
                approvals and detail visible to {form.name || 'this person'} — and to
                anybody further up.
              </p>
              <TeamPicker all={everybody.data ?? []} candidates={teamChoices}
                          chosen={team} onToggle={toggleMember}
                          note={(p) => {
                            // Ticking somebody who reports elsewhere MOVES them, and
                            // that is worth seeing before saving rather than
                            // afterwards on the other person's screen.
                            if (!p.reports_to || p.reports_to === user.user_id) {
                              return ''
                            }
                            const m = (everybody.data ?? []).find(
                              (x) => x.user_id === p.reports_to)
                            return m ? `now under ${m.name}` : ''
                          }} />
            </div>
          )}

          {/* Who may approve this person's own work.
            *
            *  Only worth asking once they can raise any: with the switch off, the
            *  answer changes nothing they will ever see. Left blank the app works it
            *  out from the tree - whoever is above them, or every approver if they
            *  are outside it - so this is the Owner overriding, not configuring. */}
          {form.can_enter_tasks && (
            <div>
              <label className="label">Their approvers</label>
              <p className="text-[11.5px] text-ink-3 mb-1.5">
                {approverChoices.length === 0
                  ? `Nobody in this firm can approve yet.`
                  : `Leave this empty and they choose from whoever is above them —
                     ${form.reports_to ? 'their own line of managers' : 'every approver, since they are under nobody'}.
                     Tick somebody to fix the answer instead.`}
              </p>
              <TeamPicker all={everybody.data ?? []} candidates={approverChoices}
                          chosen={approvers} onToggle={toggleApprover} />
            </div>
          )}

          <div>
            {/* Open to anybody now. It used to be disabled without a team, on the
                reasoning that there was nobody to raise work for - which missed the
                obvious case: themselves. What it grants depends on whether they have
                a team, and the label says which. */}
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={form.can_enter_tasks}
                     onChange={() => set('can_enter_tasks', !form.can_enter_tasks)} />
              {hasTeam ? 'Can enter tasks for their team' : 'Can enter their own tasks'}
            </label>
            <p className="text-[11.5px] text-ink-3 mt-1">
              {hasTeam
                ? `Off by default. Seeing your team comes with having one; raising and
                   changing their work is yours to grant, and to take back.`
                : `They can raise work for themselves and nobody else, and must choose
                   an approver for it. Work you assign them stays yours to change.`}
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-[13px]">
              <input type="checkbox" checked={form.active}
                     onChange={(e) => set('active', e.target.checked)} />
              Can sign in
            </label>
            <p className="text-[11.5px] text-ink-3 mt-1">
              Turning this off locks them out within 15 minutes, and leaves every task
              of theirs exactly as it is.
            </p>
          </div>

          <div className="border-t border-rule pt-3.5">
            <label className="label">{channelName}</label>
            <input className="field mono" type="tel" inputMode="tel" value={form.phone} placeholder="+91 98200 11234"
                   onChange={(e) => set('phone', e.target.value)} />

            {onTelegram ? (
              <>
                {/* On Telegram the phone is a contact detail, not the address
                    messages go to - and it is worth keeping, because it is what
                    WhatsApp would need again if the Owner ever switches back. */}
                <p className="text-[11.5px] text-ink-3 mt-1">
                  For your records. Telegram messages go to their linked account, not
                  to this number.
                </p>
                {/* There is no opt-in tick on Telegram: tapping the invite is the
                    consent record, and the person does it themselves. Their stored
                    WhatsApp consent is untouched and comes back if you switch. */}
                <div className="mt-2.5 flex items-center gap-2">
                  <span className="text-[12.5px]">
                    {user.telegram_chat_id
                      ? <>Linked{sinceLabel(user, true)}</>
                      : <>Not linked — they have not started the bot yet</>}
                  </span>
                  {!user.telegram_chat_id && (
                    <span className="ml-auto"><InviteButton user={user} /></span>
                  )}
                </div>
              </>
            ) : (
              <>
                <label className="flex items-center gap-2 text-[13px] mt-2.5">
                  <input type="checkbox" checked={form.whatsapp_opt_in}
                         onChange={(e) => set('whatsapp_opt_in', e.target.checked)} />
                  They have agreed to receive WhatsApp messages
                </label>
                <p className="text-[11.5px] text-ink-3 mt-1">
                  Ticking this records <b>you</b> confirming they agreed, not them
                  agreeing. Ask first — WhatsApp requires consent, and Meta counts
                  complaints against the number.
                </p>
              </>
            )}

            <div className={`mt-2.5 text-[12px] px-2.5 py-1.5 rounded ${
              willReceive ? 'bg-done-tint text-done-ink' : 'bg-pend-tint text-pend-ink'}`}>
              {willReceive
                ? `Will receive ${channelName} messages.`
                : `Will NOT receive ${channelName} messages: ${missing.join(', ')}.`}
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <input className="field" value={form.notes}
                   onChange={(e) => set('notes', e.target.value)} />
          </div>
        </div>

        <div className="px-5 py-3.5 border-t border-rule flex gap-2">
          <button className="btn btn-primary"
                  disabled={save.isPending || form.roles.length === 0}
                  onClick={() => { setErr(null); save.mutate() }}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </aside>
    </>
  )
}

/** Will this person receive anything, and if not, why not - live, as the form changes.
 *
 *  This mirrors `User.reachable_on` / `why_unreachable_on` in
 *  `backend/app/domain/entities.py:232-271`, and it has to stay local rather than use
 *  the server's `notifiable`: the point is that it reacts while the Owner is still
 *  typing, and the server's answer is frozen at the moment the drawer opened.
 *
 *  It previously mirrored only the WhatsApp half. On Telegram it therefore announced
 *  "not opted in" about somebody linked and working, while the table beside it showed
 *  the server's correct answer - one screen saying two things about one person. The
 *  reasons below are worded to match `why_unreachable_on` so the two agree literally.
 *
 *  `telegram_chat_id` comes from the stored person, not the form: nothing in this
 *  drawer can change it, which is exactly why the fix for it is an invite.
 */
function reachability(
  form: { active: boolean; whatsapp_opt_in: boolean; phone: string },
  user: Person, onTelegram: boolean,
) {
  const missing = onTelegram
    ? [!form.active && 'not active',
       !user.telegram_chat_id && 'has not started the bot yet']
    : [!form.active && 'not active',
       !form.whatsapp_opt_in && 'not opted in',
       !form.phone.trim() && 'no phone number']
  const reasons = missing.filter(Boolean) as string[]
  return { willReceive: reasons.length === 0, missing: reasons }
}

function sinceLabel(u: Person, onTelegram: boolean) {
  const stamp = onTelegram ? u.telegram_linked_at : u.opt_in_at
  if (!stamp) return ''
  // The firm's date, not the UTC one - the slice was a day early before 05:30.
  return ` · ${onTelegram ? 'linked' : 'since'} ${stampDate(stamp)}`
}

/** What to actually send them.
 *
 *  The dialog used to show a bare URL, which left the Owner writing the covering note
 *  themselves four times over - and a naked t.me link arriving with no explanation is
 *  exactly the sort of thing people ignore or distrust. Plain text, because it gets
 *  pasted into WhatsApp, SMS or email, none of which want markup.
 */
function inviteMessage(firstName: string, link: string, days: number): string {
  return [
    `Hi ${firstName},`,
    ``,
    `We are moving the daily task list to Telegram. Tap this link on your phone `
    + `and press Start - that connects the bot to your account, and your tasks will `
    + `arrive there from then on.`,
    ``,
    link,
    ``,
    `The link is just for you and works once. It expires in ${days} days - ask for `
    + `a new one if it has.`,
  ].join('\n')
}

function InviteButton({ user }: { user: Person }) {
  const qc = useQueryClient()
  const [invite, setInvite] = useState<Invite | null>(null)
  const [err, setErr] = useState<unknown>(null)
  const [copied, setCopied] = useState(false)
  const [copiedMsg, setCopiedMsg] = useState(false)

  const mint = useMutation({
    mutationFn: () => api.post<Invite>(`/users/${user.user_id}/telegram-invite`),
    onSuccess: (i) => { setErr(null); setInvite(i) },
    onError: setErr,
  })

  const message = invite
    ? inviteMessage(user.name.split(' ')[0], invite.link, invite.expires_in_days)
    : ''

  const close = () => {
    setInvite(null); setErr(null); setCopied(false); setCopiedMsg(false)
    // The list shows who is linked, and they may have tapped it while this was open.
    qc.invalidateQueries({ queryKey: ['users'] })
  }

  return (
    <>
      <button className="btn btn-sm" disabled={mint.isPending}
              onClick={() => mint.mutate()}>
        {mint.isPending ? '…' : 'Invite'}
      </button>

      {invite || err ? (
        <>
          <div className="fixed inset-0 bg-ink/10 z-10" onClick={close} />
          {/* Centred, so a fixed 560px loses ~85px off EACH side of a 390px screen. */}
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                          w-[calc(100vw-1.5rem)] sm:w-[560px] max-h-[90vh]
                          overflow-y-auto z-20
                          bg-surface border border-rule rounded-lg shadow-2xl">
            <div className="px-5 py-3.5 border-b border-rule flex items-center">
              <b className="text-[13px]">Invite {user.name} to Telegram</b>
              <span className="flex-1" />
              <button onClick={close}
                      className="text-ink-3 hover:text-ink text-lg px-1">×</button>
            </div>
            <div className="p-5 space-y-3">
              {err ? <ErrorNote error={err} /> : null}
              {invite ? (
                <>
                  {/* Two ways, because the two situations are different: the person
                      is standing here (scan it), or they are not (send them the
                      message). Neither is a fallback for the other. */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="shrink-0 text-center">
                      <div className="border border-rule rounded-md p-2 bg-white">
                        <QrCode value={invite.link} size={148}
                                title={`Telegram invite for ${user.name}`} />
                      </div>
                      <p className="text-[11px] text-ink-3 mt-1.5 w-[168px]">
                        With you now? Have them scan this.
                      </p>
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <p className="text-[12.5px]">
                        Not with you? Send {user.name.split(' ')[0]} this message. It
                        explains what the link is for, which a bare link does not.
                      </p>
                      {/* `bg-paper`: this said `bg-surface-2`, which is not a
                          Tailwind token and whose `--c-surface-2` is not defined
                          either - so the box has always been plain white rather
                          than the recessed grey it was meant to be. */}
                      <textarea readOnly rows={7} value={message}
                                onFocus={(e) => e.currentTarget.select()}
                                className="w-full rounded-md border border-rule
                                           px-2.5 py-2 text-[11.5px] leading-snug
                                           bg-paper" />
                      <div className="flex gap-2">
                        <button className="btn btn-primary" onClick={() => {
                          navigator.clipboard?.writeText(message)
                            .then(() => setCopiedMsg(true))
                            .catch(() => setCopiedMsg(false))
                        }}>{copiedMsg ? 'Message copied' : 'Copy message'}</button>
                        <button className="btn" onClick={() => {
                          // Clipboard needs a secure context. This app is served over
                          // HTTPS, but a local http:// deploy would throw — so the
                          // field below stays selectable rather than the only path
                          // being one that can fail silently.
                          navigator.clipboard?.writeText(invite.link)
                            .then(() => setCopied(true)).catch(() => setCopied(false))
                        }}>{copied ? 'Copied' : 'Copy link only'}</button>
                      </div>
                    </div>
                  </div>
                  <input readOnly value={invite.link}
                         onFocus={(e) => e.currentTarget.select()}
                         className="field mono text-[11.5px]" />
                  <p className="text-[11.5px] text-ink-3">
                    Works once, and expires in {invite.expires_in_days} days. Making a
                    new invite for {user.name.split(' ')[0]} cancels this one.
                  </p>
                  <p className="text-[11.5px] text-ink-3">
                    This is the only time the link is shown — the app keeps only a hash
                    of it, so it cannot show you the link again.
                  </p>
                </>
              ) : null}
            </div>
            <div className="px-5 py-3 border-t border-rule">
              <button className="btn" onClick={close}>Done</button>
            </div>
          </div>
        </>
      ) : null}
    </>
  )
}
